import { Button, Input, Typography } from '@breetometer/components';
import { InfoIcon, NotificationBannerAlertIcon } from 'BreetAssets';
import { DialogAccessCredentials, ModalParts } from 'BreetComponents';
import { type IUseAccessCredentialsRequest, OTPLENGTH } from 'BreetHelpers';
import { useAccessCredentials, useFormData, useFormStage } from 'BreetHooks';
import { closeModal, selectDeveloperIntegration, useAppDispatch, useAppSelector, useSetIpAddressMutation } from 'BreetRedux';
import { useCallback } from 'react';

import {
	type IDialogEditDeveloperIpAddressChildProps,
	IPAddressSchema,
	type IPSTAGE,
} from './DialogSetDeveloperIpAddresses.helpers';

const Child = ({
	title,
	description,
	noModal,
	ipAddress,
	label,
	cta,
	disabled,
	accessCredentialsVerificationId,
}: IDialogEditDeveloperIpAddressChildProps) => {
	const { navigator, isStageActive } = useFormStage<IPSTAGE>('ip-address', ['ip-address', 'otp-verification']);
	const dispatch = useAppDispatch();

	const [setIpAddressMutation, { isLoading: setIpAddressLoading }] = useSetIpAddressMutation();

	const developerIntegrationData = useAppSelector(selectDeveloperIntegration);

	const ipAddressFormDataContextProps = useFormData(IPAddressSchema, { ipAddress }, { mode: 'onChange' });

	const submitIpAddress = useCallback(
		(data?: IUseAccessCredentialsRequest) => {
			const newIpAddress = ipAddressFormDataContextProps.hookFormProps.getValues('ipAddress');

			const existingIpAddresses = developerIntegrationData.data?.data?.ipAddresses ?? [];

			if (existingIpAddresses.filter((ip) => ip !== ipAddress).includes(newIpAddress)) {
				ipAddressFormDataContextProps.hookFormProps.setError('ipAddress', {
					message: 'This IP address is already configured',
				});
				return;
			}

			const ipAddresses = ipAddress
				? existingIpAddresses.map((ip) => (ip === ipAddress ? newIpAddress : ip))
				: [...existingIpAddresses, newIpAddress];

			setIpAddressMutation({ ipAddresses })
				.unwrap()
				.then(() => {
					ipAddressFormDataContextProps.hookFormProps.reset({ ipAddress: '' });
					data?.onSuccess?.();

					if (ipAddress) {
						dispatch(closeModal('editDeveloperIpAddress'));
					}
				})
				.catch((err: unknown) => {
					data?.onError(err);
				});
		},
		[
			developerIntegrationData.data?.data?.ipAddresses,
			dispatch,
			ipAddress,
			ipAddressFormDataContextProps.hookFormProps,
			setIpAddressMutation,
		]
	);

	const { handleVerification: handleSetIpAddressVerification, component: SetIpAddressComponent } = useAccessCredentials({
		id: accessCredentialsVerificationId,
		mode: 'otp',
		submitRequest: submitIpAddress,
		dialogMessage: 'Verifying OTP...',
		title: 'Verify OTP',
		description: `Enter the ${OTPLENGTH}-digit OTP sent to your email to set the IP address.`,
		type: 'verify',
		render: noModal ? undefined : (props) => <DialogAccessCredentials {...props} />,
	});

	const handleSubmit = useCallback(() => {
		if (!noModal) {
			navigator.goTo('otp-verification')();
		}

		handleSetIpAddressVerification();
	}, [handleSetIpAddressVerification, noModal, navigator]);

	return (
		<ModalParts.Wrapper>
			<ModalParts.Stage hide={!isStageActive('ip-address')}>
				<div className='appdeveloper_card column'>
					<div className='appdeveloper_cardheader'>
						{title && (
							<div className='appdeveloper_cardtitle'>
								<Typography as='h2'>{title}</Typography>
								<InfoIcon className='svg-dark' />
							</div>
						)}
						{!disabled ? (
							<>{description && <Typography>{description}</Typography>}</>
						) : (
							<div className='dialog-webhook-verification_note'>
								<NotificationBannerAlertIcon
									status='warning'
									className='status-icon-pending'
								/>
								<Typography>You have reached the maximum number of IP addresses</Typography>
							</div>
						)}
					</div>
					<form
						className='appdeveloper_form'
						onSubmit={ipAddressFormDataContextProps.hookFormProps.handleSubmit(handleSubmit)}
					>
						<Input
							label={label}
							id='ipAddress'
							required
							widthType='full'
							autoComplete='off'
							{...ipAddressFormDataContextProps.hookFormProps.register('ipAddress')}
							errorMessage={ipAddressFormDataContextProps.getFieldError('ipAddress')?.message}
							disabled={disabled}
						/>
						<Button
							label={cta}
							disabled={
								!ipAddressFormDataContextProps.isFormCompleted ||
								!ipAddressFormDataContextProps.hookFormProps.formState.isDirty ||
								setIpAddressLoading ||
								disabled
							}
							className='theme-background-none bg-primary-400'
							type='submit'
							isLoading={setIpAddressLoading}
						/>
					</form>
				</div>
			</ModalParts.Stage>

			<ModalParts.Stage hide={!isStageActive('otp-verification')}>{SetIpAddressComponent}</ModalParts.Stage>
		</ModalParts.Wrapper>
	);
};

export default Child;
